/**
 * 百度矢量瓦片图层封装 (VectorTileLayer)
 * 用于展示 SD Link 数据
 *
 * 用法：
 *   const layer = createVectorTileLayer(map, { url, minZoom, maxZoom, opacity, styleMap });
 *   // layer 是 BMapGL.VectorTileLayer 实例
 *   setVectorTileLayerVisible(map, layer, false); // 隐藏
 *   setVectorTileLayerVisible(map, layer, true);  // 显示
 *
 * 产品决定不展示时，只需删除调用处的代码，此模块可整体移除
 */

interface DisplayOptions {
  background?: boolean;
  poly?: boolean;
  line?: boolean;
  poi?: boolean;
}

interface StyleItem {
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  fillColor?: string;
  fillOpacity?: number;
  displayoptions?: DisplayOptions;
}

interface VectorTileLayerOptions {
  url: string;
  minZoom?: number;
  maxZoom?: number;
  opacity?: number;
  useThumbData?: boolean;
  styleMap?: Record<number, StyleItem>;
  displayOptions?: DisplayOptions;
}

const DEFAULT_STYLE_MAP = {
  99: {
    strokeColor: '#00FF00',
    strokeWeight: 2,
    strokeOpacity: 0.85,
    displayoptions: {
      background: false,
    },
  },
};

const DEFAULT_DISPLAY_OPTIONS = {
  background: false,
};

/**
 * 创建并添加矢量瓦片图层
 */
export function createVectorTileLayer(
  map: any,
  options: VectorTileLayerOptions
): any {
  const {
    url,
    minZoom = 19,
    maxZoom = 19,
    opacity = 1,
    useThumbData = true,
    styleMap = DEFAULT_STYLE_MAP,
    displayOptions = DEFAULT_DISPLAY_OPTIONS,
  } = options;

  const vectorTileLayer = new window.BMapGL.VectorTileLayer({
    url,
    minZoom,
    maxZoom,
    opacity,
    useThumbData,
    styleMap,
    displayOptions,
  });

  map.addTileLayer(vectorTileLayer);
  return vectorTileLayer;
}

export function removeVectorTileLayer(map: any, layer: any): void {
  if (layer && map) {
    map.removeTileLayer(layer);
  }
}

/**
 * 切换矢量瓦片图层显隐
 *
 * 对 BMapGL.VectorTileLayer 实测：
 * - layer.show()/hide() 存在但无视觉反应
 * - layer.setOpacity() 存在但无视觉反应
 * - 唯一有效的方式是 map.removeTileLayer / map.addTileLayer
 *
 * 因此本函数直接使用 remove/add 复用同一图层实例，
 * 切换时会有瓦片刷新感，但这是百度地图该图层类的标准做法。
 */
export function setVectorTileLayerVisible(
  map: any,
  layer: any,
  visible: boolean
): any {
  if (!layer || !map) return layer;

  if (visible) {
    map.addTileLayer(layer);
  } else {
    map.removeTileLayer(layer);
  }
  return layer;
}
